import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import {
  ApplicationUsersLoginLoginUserResponse,
  UsersService,
  WebApiEndpointsUsersLoginRequest,
} from '@crm/api';
import { jwtDecode } from 'jwt-decode';
import {
  LEGACY_EXPIRATION_KEY,
  PERSISTENT_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_KEY,
  readAccessToken,
  readRefreshToken,
  sessionStorageFor,
} from './token-storage';

/** Set by the API when the user is a director restricted to one employer. */
const EMPLOYER_CLAIM = 'employer';

const ROLE_CLAIM =
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

const USER_ID_CLAIM =
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly usersService = inject(UsersService);

  private tokenSubject = new BehaviorSubject<string | null>(readAccessToken());
  public token$ = this.tokenSubject.asObservable();

  /** In-flight renewal, shared so concurrent 401s trigger a single call. */
  private refreshInFlight$: Observable<string> | null = null;

  private hasRole(token: string | null, ...roles: string[]): boolean {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token) as Record<string, unknown>;
      // Le claim peut être 'role' ou une URI spécifique selon la conf du serveur
      const claim =
        (decoded['role'] as string | string[] | undefined) ??
        (decoded[ROLE_CLAIM] as string | string[] | undefined);
      if (Array.isArray(claim)) {
        return roles.some((role) => claim.includes(role));
      }
      return roles.some((role) => claim === role);
    } catch (e) {
      console.error('Error decoding token', e);
      return false;
    }
  }

  /** The signed-in user's own identifier, so a screen can tell their record from the others. */
  public userId$ = this.token$.pipe(
    map((token) => {
      if (!token) return null;
      try {
        const decoded = jwtDecode(token) as Record<string, unknown>;
        return (decoded[USER_ID_CLAIM] as string | undefined) ?? null;
      } catch {
        return null;
      }
    })
  );

  public isAdminOrDirector$ = this.token$.pipe(
    map((token) => this.hasRole(token, 'Administrator', 'Director'))
  );

  public isAdmin$ = this.token$.pipe(
    map((token) => this.hasRole(token, 'Administrator'))
  );

  public isSalesRepresentative$ = this.token$.pipe(
    map((token) => this.hasRole(token, 'SalesRepresentative'))
  );

  /**
   * True for a director restricted to one employer. The API only puts the claim in the token
   * when the restriction applies, so its mere presence is the signal.
   */
  public isEmployerScoped$ = this.token$.pipe(
    map((token) => this.hasClaim(token, EMPLOYER_CLAIM))
  );

  private hasClaim(token: string | null, claim: string): boolean {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token) as Record<string, unknown>;
      return decoded[claim] !== undefined && decoded[claim] !== null;
    } catch {
      return false;
    }
  }

  get token(): string | null {
    return this.tokenSubject.value;
  }

  get refreshToken(): string | null {
    return readRefreshToken();
  }

  login(
    credentials: WebApiEndpointsUsersLoginRequest & { rememberMe?: boolean }
  ): Observable<ApplicationUsersLoginLoginUserResponse> {
    const rememberMe = credentials.rememberMe ?? true;

    // Set before storing anything, since it selects the storage that will be written to.
    localStorage.setItem(PERSISTENT_KEY, String(rememberMe));

    return this.usersService
      .usersLoginPost({
        webApiEndpointsUsersLoginRequest: { ...credentials, rememberMe },
      })
      .pipe(tap((response) => this.setSession(response)));
  }

  logout(): void {
    const refreshToken = this.refreshToken;

    // Fire and forget: the local session must end even if the call fails.
    if (refreshToken) {
      this.usersService
        .usersLogoutPost({
          webApiEndpointsUsersLogoutRequest: { refreshToken },
        })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }

    this.clearSession();
  }

  isLoggedIn(): boolean {
    return !!this.token || !!this.refreshToken;
  }

  /**
   * Renews the access token from the refresh token. Concurrent callers share one request so a
   * burst of 401s does not rotate the refresh token several times, which would revoke it.
   */
  refreshSession(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.refreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInFlight$ = this.usersService
      .usersRefreshPost({
        webApiEndpointsUsersRefreshRequest: { refreshToken },
      })
      .pipe(
        tap((response) => this.setSession(response)),
        map((response) => response.accessToken as string),
        catchError((error) => {
          this.clearSession();
          return throwError(() => error);
        }),
        tap({
          finalize: () => {
            this.refreshInFlight$ = null;
          },
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.refreshInFlight$;
  }

  /**
   * Renews the session at application start when an access token is absent or already expired,
   * so a returning user lands signed in instead of on the login page.
   */
  restoreSession(): Observable<unknown> {
    if (!this.refreshToken) {
      return of(null);
    }

    if (this.token && !this.isAccessTokenExpired(this.token)) {
      return of(null);
    }

    return this.refreshSession().pipe(catchError(() => of(null)));
  }

  private isAccessTokenExpired(token: string): boolean {
    try {
      const { exp } = jwtDecode(token) as { exp?: number };
      if (!exp) return true;
      // One minute of margin so a call is not sent with a token about to expire.
      return exp * 1000 <= Date.now() + 60_000;
    } catch {
      return true;
    }
  }

  private setSession(response: ApplicationUsersLoginLoginUserResponse): void {
    const storage = sessionStorageFor();

    if (response.accessToken) {
      storage.setItem(TOKEN_KEY, response.accessToken);
      this.tokenSubject.next(response.accessToken);
    }

    if (response.refreshToken) {
      storage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    }
  }

  private clearSession(): void {
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem(TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(LEGACY_EXPIRATION_KEY);
    }
    localStorage.removeItem(PERSISTENT_KEY);
    this.tokenSubject.next(null);
  }

  /** Chains a request behind a successful renewal. */
  withRefreshedToken<T>(request: (token: string) => Observable<T>): Observable<T> {
    return this.refreshSession().pipe(switchMap((token) => request(token)));
  }
}
